import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import type { ConversionSettings, TargetMimeType } from '../types';

const CORE_BASE_URL = '/ffmpeg';

let ffmpeg: FFmpeg | null = null;
let loadPromise: Promise<FFmpeg> | null = null;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const getCrf = (compression: number): string => {
  const normalized = clamp(compression, 10, 100);
  return String(Math.round(40 - normalized * 0.24));
};

const getScaleFilter = (resolution: ConversionSettings['resolution']): string | null => {
  if (resolution === 'original') return null;
  const height = resolution.replace('p', '');
  return `scale=-2:${height}`;
};

const CANVAS_SIZE: Record<Exclude<ConversionSettings['canvasPreset'], 'original'>, { width: number; height: number }> = {
  'instagram-reel': { width: 1080, height: 1920 },
  youtube: { width: 1920, height: 1080 },
  square: { width: 1080, height: 1080 },
  'portrait-feed': { width: 1080, height: 1350 },
};

const getCanvasFilter = (settings: ConversionSettings): string | null => {
  if (settings.canvasPreset === 'original') return null;
  const { width, height } = CANVAS_SIZE[settings.canvasPreset];
  if (settings.fitMode === 'crop') {
    return `scale=${width}:${height}:force_original_aspect_ratio=increase,crop=${width}:${height},setsar=1`;
  }
  if (settings.fitMode === 'pad') {
    return `scale=${width}:${height}:force_original_aspect_ratio=decrease,pad=${width}:${height}:(ow-iw)/2:(oh-ih)/2:color=black,setsar=1`;
  }
  return `[0:v]scale=${width}:${height}:force_original_aspect_ratio=increase,crop=${width}:${height},boxblur=24:1[bg];[0:v]scale=${width}:${height}:force_original_aspect_ratio=decrease[fg];[bg][fg]overlay=(W-w)/2:(H-h)/2,setsar=1`;
};

const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop();
  return ext ? ext.toLowerCase() : '';
};

const getInputName = (file: File): string => {
  const ext = getFileExtension(file.name) || 'mp4';
  return `input.${ext}`;
};

const getOutputName = (targetFormat: TargetMimeType): string => {
  if (targetFormat === 'video/quicktime') return 'output.mov';
  if (targetFormat === 'video/webm') return 'output.webm';
  if (targetFormat === 'video/x-matroska') return 'output.mkv';
  if (targetFormat === 'video/x-msvideo') return 'output.avi';
  if (targetFormat === 'video/x-m4v') return 'output.m4v';
  if (targetFormat === 'image/gif') return 'output.gif';
  return 'output.mp4';
};

const loadFfmpeg = async (): Promise<FFmpeg> => {
  if (ffmpeg) return ffmpeg;
  if (!loadPromise) {
    const instance = new FFmpeg();
    loadPromise = Promise.all([
      toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
      toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
    ]).then(([coreURL, wasmURL]) => instance.load({ coreURL, wasmURL })).then(() => {
      ffmpeg = instance;
      return instance;
    });
  }
  return loadPromise;
};

const buildCommand = (
  inputName: string,
  outputName: string,
  targetFormat: TargetMimeType,
  settings: ConversionSettings
): string[] => {
  const canvasFilter = getCanvasFilter(settings);
  const scale = getScaleFilter(settings.resolution);
  const scaleArgs = canvasFilter
    ? [canvasFilter.includes('[0:v]') ? '-filter_complex' : '-vf', canvasFilter]
    : scale ? ['-vf', scale] : [];

  if (targetFormat === 'video/webm') {
    return [
      '-i', inputName,
      ...scaleArgs,
      '-c:v', 'libvpx-vp9',
      '-b:v', '0',
      '-crf', getCrf(settings.compression),
      '-c:a', 'libopus',
      '-b:a', '128k',
      outputName,
    ];
  }

  if (targetFormat === 'image/gif') {
    throw new Error('GIF conversion uses a two-pass palette workflow.');
  }

  if (targetFormat === 'video/x-msvideo') {
    return [
      '-i', inputName,
      ...scaleArgs,
      '-c:v', 'mpeg4',
      '-q:v', String(Math.round(16 - clamp(settings.compression, 10, 100) * 0.125)),
      '-c:a', 'libmp3lame',
      '-b:a', '128k',
      outputName,
    ];
  }

  const fastStartArgs = targetFormat === 'video/mp4' || targetFormat === 'video/x-m4v'
    ? ['-movflags', '+faststart']
    : [];

  return [
    '-i', inputName,
    ...scaleArgs,
    '-c:v', 'libx264',
    '-preset', 'veryfast',
    '-crf', getCrf(settings.compression),
    '-c:a', 'aac',
    '-b:a', '128k',
    ...fastStartArgs,
    outputName,
  ];
};

const buildGifCommands = (inputName: string, outputName: string, settings: ConversionSettings): string[][] => {
  const scale = getScaleFilter(settings.resolution) ?? 'scale=-2:480';
  const speed = clamp(settings.gifSpeed, 0.25, 4);
  const fps = Math.round(clamp(settings.gifFps, 4, 30));
  const speedFilter = speed === 1 ? '' : `setpts=PTS/${speed},`;
  const normalizedVideo = `${speedFilter}fps=${fps},${scale}:flags=lanczos`;

  return [
    [
      '-i', inputName,
      '-vf', `${normalizedVideo},palettegen`,
      'palette.png',
    ],
    [
      '-i', inputName,
      '-i', 'palette.png',
      '-filter_complex', `${normalizedVideo}[x];[x][1:v]paletteuse`,
      '-loop', '0',
      outputName,
    ],
  ];
};

const startSmoothProgress = (
  onProgress: ((progress: number) => void) | undefined,
  maxProgress: number,
  step = 8,
  intervalMs = 1500
): (() => void) => {
  if (!onProgress) return () => undefined;
  let current = 0;
  const timer = window.setInterval(() => {
    current = Math.min(maxProgress, current + step);
    onProgress(current);
    if (current >= maxProgress) window.clearInterval(timer);
  }, intervalMs);
  return () => window.clearInterval(timer);
};

export const convertVideoWithFfmpeg = async (
  file: File,
  targetFormat: TargetMimeType,
  settings: ConversionSettings,
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  const inputName = getInputName(file);
  const outputName = getOutputName(targetFormat);
  const ffmpegLogs: string[] = [];
  let progressBase = 0;
  let progressRange = 100;
  let lastProgress = -1;
  const emitProgress = (value: number): void => {
    if (!onProgress) return;
    const rounded = Math.round(clamp(value, 0, 100));
    if (rounded !== lastProgress) {
      lastProgress = rounded;
      onProgress(rounded);
    }
  };

  try {
    const instance = await loadFfmpeg();
    instance.on('log', ({ message }) => {
      if (message) ffmpegLogs.push(message);
    });
    if (onProgress) {
      instance.on('progress', ({ progress }) => {
        emitProgress(progressBase + clamp(progress, 0, 1) * progressRange);
      });
    }
    await instance.writeFile(inputName, await fetchFile(file));
    if (targetFormat === 'image/gif') {
      const [paletteCommand, gifCommand] = buildGifCommands(inputName, outputName, settings);
      progressBase = 0;
      progressRange = 50;
      const stopPaletteProgress = startSmoothProgress(emitProgress, 45);
      try {
        await instance.exec(paletteCommand);
      } finally {
        stopPaletteProgress();
      }
      emitProgress(50);
      progressBase = 50;
      progressRange = 50;
      await instance.exec(gifCommand);
      emitProgress(100);
    } else {
      await instance.exec(buildCommand(inputName, outputName, targetFormat, settings));
    }
    const data = await instance.readFile(outputName);
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    if (bytes.byteLength === 0) {
      throw new Error('FFmpeg produced an empty output file.');
    }
    const outputBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    return new Blob([outputBuffer], { type: targetFormat });
  } catch (err) {
    const message = err instanceof Error
      ? err.message
      : ffmpegLogs.at(-1) ?? String(err || 'Unknown conversion error.');
    throw new Error(`[${file.name}] ${message}`, { cause: err });
  }
};

export const resetFfmpegForTests = (): void => {
  ffmpeg = null;
  loadPromise = null;
};
